import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';

import styled from 'styled-components';

import { LoadingIndicator } from 'atoms';

import { GroupColors } from './groupColors';

interface ITabStateViewProps {
  loading: boolean;
  error?: ApolloError;
  isEmpty: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}

/**
 * TabStateView — shared terminal-state wrapper for every GroupDetail tab
 * (Overview, Students, Schedule, Zoom, PandaDoc, Materials, Finances).
 *
 * It distinguishes the three terminal states a tab query can land in and
 * keeps them visually distinct (Requirement 14):
 *
 *   1. Loading — the initial load (no usable data yet). Reuses the shared
 *      `LoadingIndicator` atom, consistent with the other tabs.
 *   2. Error   — an `ApolloError` is present. Renders a danger-toned panel
 *      that is deliberately different from the empty state, with a Retry
 *      control that calls `onRetry` (typically the hook's `refetch`).
 *   3. Empty   — the query resolved with no rows and no error. Renders a
 *      muted/neutral panel.
 *
 * When none of the above hold, the populated `children` are rendered.
 *
 * Precedence is loading → error → empty → children. The caller decides when
 * `loading` means "no data yet" (initial load) versus a background refresh,
 * so a background refresh can keep showing populated content rather than the
 * loader.
 *
 * All user-facing copy resolves through i18next under `pages.groupDetail.*`
 * and every color comes from the shared `GroupColors` theme tokens, so no
 * hardcoded hex literal enters the GroupDetail subtree (Requirement 11).
 */
export const TabStateView = memo(
  ({ loading, error, isEmpty, onRetry, children }: ITabStateViewProps) => {
    const { t } = useTranslation();

    if (loading) {
      return (
        <CenterState>
          <LoadingIndicator />
        </CenterState>
      );
    }

    if (error) {
      return (
        <ErrorPanel role="alert">
          <ErrorMessage>{t('pages.groupDetail.state.error')}</ErrorMessage>
          <RetryButton type="button" onClick={onRetry}>
            {t('pages.groupDetail.state.retry')}
          </RetryButton>
        </ErrorPanel>
      );
    }

    if (isEmpty) {
      return (
        <EmptyPanel>
          <EmptyMessage>{t('pages.groupDetail.state.empty')}</EmptyMessage>
        </EmptyPanel>
      );
    }

    return <>{children}</>;
  },
);

const CenterState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  padding: 24px;
`;

const ErrorPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-height: 160px;
  padding: 24px;
  text-align: center;
  border: 1px solid ${GroupColors.danger};
  border-radius: 8px;
  background: ${GroupColors.surface};
`;

const ErrorMessage = styled.div`
  color: ${GroupColors.danger};
  font-size: 14px;
  font-weight: 600;
`;

const RetryButton = styled.button`
  border: 1px solid ${GroupColors.accent};
  background: ${GroupColors.accent};
  color: ${GroupColors.surface};
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover:not(:disabled) {
    background: ${GroupColors.accentStrong};
    border-color: ${GroupColors.accentStrong};
  }
`;

const EmptyPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  padding: 24px;
  text-align: center;
  border: 1px dashed ${GroupColors.border};
  border-radius: 8px;
  background: ${GroupColors.surfaceMuted};
`;

const EmptyMessage = styled.div`
  color: ${GroupColors.textMuted};
  font-size: 13px;
  font-weight: 500;
`;
